/**
 * NotificationsBell — bell icon trigger + drop-down panel for the TopBar.
 *
 * Accessibility mirrors ProfileMenu:
 *   - aria-haspopup="menu" on trigger
 *   - role="menu" on panel
 *   - role="menuitem" on each notification row
 *   - Escape closes + returns focus to trigger
 *   - Outside click closes
 *
 * Animation: same fade+translateY as ProfileMenu.
 * Motion: gated on prefers-reduced-motion via CSS @media.
 */

import { useCallback, useEffect, useRef } from 'react'
import type { KeyboardEvent } from 'react'
import { formatDistanceToNow, parseISO } from 'date-fns'
import type { Role } from '@lms/shared-types'
import { Icon } from '../primitives/Icon'
import { useNotifications } from '../../lib/use-notifications'
import type { Notification } from '../../lib/notifications'

// ── Internal state ─────────────────────────────────────────────────────────────

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'

export interface NotificationsBellProps {
  role: Role
  /** When the bell sits on the dark gradient top-bar, flip the trigger icon +
   *  hover to white so it reads. The dropdown panel is unaffected. */
  onDark?: boolean
}

const BADGE_CAP = 9

function formatOccurredAt(iso: string): string {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true })
  } catch {
    return ''
  }
}

export function NotificationsBell({ role, onDark = false }: Readonly<NotificationsBellProps>): JSX.Element {
  const { notifications, unreadCount, isLoading, markRead, markAllRead } = useNotifications(role)
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // ── close on outside click ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent): void {
      if (
        panelRef.current &&
        triggerRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // ── keyboard: Escape closes and returns focus ───────────────────────────────
  const handlePanelKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const items = Array.from(
          panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
        )
        const idx = items.findIndex((el) => el === document.activeElement)
        const next =
          e.key === 'ArrowDown'
            ? (idx + 1) % items.length
            : (idx - 1 + items.length) % items.length
        items[next]?.focus()
      }
    },
    [],
  )

  // ── when panel opens, focus first item ─────────────────────────────────────
  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')
      first?.focus()
    }, 0)
    return () => clearTimeout(id)
  }, [open])

  const toggle = useCallback((): void => setOpen((prev) => !prev), [])

  const handleRowClick = useCallback(
    (n: Notification): void => {
      markRead(n.id)
      setOpen(false)
      if (n.to) {
        void navigate({ to: n.to })
      }
    },
    [markRead, navigate],
  )

  const handleMarkAllRead = useCallback(
    (e: React.MouseEvent): void => {
      e.preventDefault()
      markAllRead()
    },
    [markAllRead],
  )

  const badgeLabel = unreadCount > BADGE_CAP ? `${BADGE_CAP}+` : String(unreadCount)

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      data-testid="notifications-bell-container"
    >
      <style>{`
        @keyframes lms-notifications-panel-in {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes lms-notifications-panel-in {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
        }
      `}</style>

      {/* ── Trigger ─────────────────────────────────────────────────────────── */}
      <button
        ref={triggerRef}
        type="button"
        data-testid="notifications-bell-trigger"
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : 'Notifications'
        }
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        style={{
          position: 'relative',
          minWidth: 44,
          minHeight: 44,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: '1px solid transparent',
          borderRadius: 8,
          color: onDark ? 'var(--surface)' : 'var(--text-2)',
          cursor: 'pointer',
          transition: 'background var(--motion-duration-base) var(--motion-ease-out)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = onDark
            ? 'rgba(255, 255, 255, 0.14)'
            : 'var(--surface-2)'
          e.currentTarget.style.borderColor = onDark
            ? 'rgba(255, 255, 255, 0.24)'
            : 'var(--border)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent'
          e.currentTarget.style.borderColor = 'transparent'
        }}
      >
        <Icon name="notifications" size={22} aria-hidden />

        {/* ── Unread badge ──────────────────────────────────────────────────── */}
        {unreadCount > 0 && (
          <span
            data-testid="notifications-badge"
            aria-hidden
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              boxSizing: 'border-box',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 17,
              height: 17,
              padding: '0 4px',
              background: 'var(--danger)',
              color: 'var(--surface)',
              border: '1.5px solid var(--surface)',
              borderRadius: 9,
              fontSize: 10,
              fontFamily: 'var(--font-mono)',
              fontWeight: 600,
              lineHeight: 1,
              pointerEvents: 'none',
            }}
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {/* ── Dropdown panel ──────────────────────────────────────────────────── */}
      {open && (
        <div
          ref={panelRef}
          role="menu"
          data-testid="notifications-panel"
          onKeyDown={handlePanelKeyDown}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: 320,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: 'var(--shadow-2)',
            zIndex: 100,
            overflow: 'hidden',
            animation: 'lms-notifications-panel-in var(--motion-duration-base) var(--motion-ease-out)',
          }}
        >
          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px 10px',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                role="menuitem"
                data-testid="notifications-mark-all-read"
                onClick={handleMarkAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 12,
                  color: 'var(--steel-blue)',
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                }}
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* ── Body ─────────────────────────────────────────────────────────── */}
          <div
            style={{
              overflowY: 'auto',
              maxHeight: '60vh',
            }}
          >
            {isLoading ? (
              <SkeletonRows />
            ) : notifications.length === 0 ? (
              <EmptyState />
            ) : (
              notifications.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onClick={handleRowClick}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────────────

interface NotificationRowProps {
  notification: Notification
  onClick: (n: Notification) => void
}

function NotificationRow({ notification, onClick }: Readonly<NotificationRowProps>): JSX.Element {
  const [hover, setHover] = useState(false)
  const isUnread = !notification.read

  return (
    <div
      role="menuitem"
      tabIndex={0}
      data-testid={`notification-row-${notification.id}`}
      onClick={() => onClick(notification)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick(notification)
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 16px',
        cursor: notification.to ? 'pointer' : 'default',
        background: hover ? 'var(--surface-2)' : 'transparent',
        transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
        borderBottom: '1px solid var(--border)',
        outline: 'none',
      }}
    >
      {/* Unread dot */}
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          marginTop: 5,
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: isUnread ? 'var(--steel-blue)' : 'transparent',
          border: isUnread ? 'none' : '1px solid var(--border)',
        }}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-sans)',
            fontSize: 13,
            color: isUnread ? 'var(--text)' : 'var(--text-2)',
            fontWeight: isUnread ? 500 : 400,
            lineHeight: 1.45,
          }}
        >
          {notification.message}
        </p>
        <p
          style={{
            margin: '3px 0 0',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--text-3)',
          }}
        >
          {formatOccurredAt(notification.occurredAt)}
        </p>
      </div>
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div
      data-testid="notifications-empty"
      style={{
        padding: '32px 16px',
        textAlign: 'center',
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        color: 'var(--text-3)',
        lineHeight: 1.5,
      }}
    >
      Nothing new. Carry on.
    </div>
  )
}

function SkeletonRows(): JSX.Element {
  return (
    <div data-testid="notifications-skeleton">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '10px 16px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div
            aria-hidden
            style={{
              flexShrink: 0,
              marginTop: 5,
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: 'var(--border)',
            }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                height: 13,
                width: '80%',
                borderRadius: 4,
                background: 'var(--border)',
                marginBottom: 6,
              }}
            />
            <div
              style={{
                height: 11,
                width: '40%',
                borderRadius: 4,
                background: 'var(--border)',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}
